import { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { MyCrossword } from "mycrossword";
import "mycrossword/style.css";
import { useMutation, useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import type { GuardianPuzzle } from "../lib/converters/nytToGuardian";
import { ConfettiPopup } from "./ConfettiPopup";

// Types from mycrossword (not exported from main entry)
type CellPosition = {
  col: number;
  row: number;
};

type CellChange = {
  pos: CellPosition;
  guess?: string;
  previousGuess?: string;
};

type CellFocus = {
  pos: CellPosition;
  clueId: string;
};

type GuessGrid = {
  value: string[][];
};

interface Props {
  sessionId: Id<"sessions">;
  userId: string;
  displayName: string;
  puzzleData: GuardianPuzzle;
  initialGrid: Array<Array<{ value: string; pencil: boolean }>>;
}

// Convert Convex grid format to GuessGrid format for mycrossword
function convexGridToGuessGrid(
  grid: Array<Array<{ value: string; pencil: boolean }>>
): GuessGrid {
  return {
    value: grid.map((col) => col.map((cell) => cell.value || "")),
  };
}

// Deep equality check for grids
function gridsEqual(a: GuessGrid | undefined, b: GuessGrid | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return a === b;
  if (a.value.length !== b.value.length) return false;

  for (let col = 0; col < a.value.length; col++) {
    if (a.value[col].length !== b.value[col].length) return false;
    for (let row = 0; row < a.value[col].length; row++) {
      if (a.value[col][row] !== b.value[col][row]) return false;
    }
  }
  return true;
}

// Check if puzzle is complete by comparing grid with solutions
function isPuzzleComplete(
  grid: Array<Array<{ value: string; pencil: boolean }>> | undefined,
  puzzleData: GuardianPuzzle
): boolean {
  if (!grid || !puzzleData.entries) return false;

  // Build solution grid from puzzle entries
  const { rows, cols } = puzzleData.dimensions;
  const solutionGrid: string[][] = Array.from({ length: cols }, () =>
    Array(rows).fill("")
  );

  // Fill solution grid from entries
  for (const entry of puzzleData.entries) {
    const { position, solution, direction } = entry;
    if (!position || !solution) continue;

    for (let i = 0; i < solution.length; i++) {
      const x = direction === "across" ? position.x + i : position.x;
      const y = direction === "down" ? position.y + i : position.y;

      if (x >= 0 && x < cols && y >= 0 && y < rows) {
        solutionGrid[x][y] = solution[i].toUpperCase();
      }
    }
  }

  // Compare current grid with solution
  for (let x = 0; x < cols; x++) {
    for (let y = 0; y < rows; y++) {
      const currentValue = grid[x]?.[y]?.value?.toUpperCase() || "";
      const solutionValue = solutionGrid[x][y];

      // If there's a solution for this cell and it doesn't match, puzzle is incomplete
      if (solutionValue && currentValue !== solutionValue) {
        return false;
      }
    }
  }

  return true;
}

export function CollaborativeCrossword({
  sessionId,
  userId,
  displayName,
  puzzleData,
  initialGrid,
}: Props) {
  // Subscribe to game state for remote updates
  const gameState = useQuery(api.game.getState, { sessionId });
  const presences = useQuery(api.presence.getActive, { sessionId });

  // Error check mode state
  const [errorCheckMode, setErrorCheckMode] = useState(false);

  // Congratulations popup state
  const [showCongrats, setShowCongrats] = useState(false);
  const hasShownCongrats = useRef(false);

  // Mutations
  const updateMultipleCells = useMutation(api.game.updateMultipleCells);
  const heartbeat = useMutation(api.presence.heartbeat);

  // Initial grid - only computed ONCE on mount
  const initialLoadGrid = useMemo(
    () => convexGridToGuessGrid(initialGrid),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Empty deps - only runs once on mount, prevents re-initialization
  );

  // Convert gameState to updateGrid for remote updates
  // Only update when grid data actually changes to avoid unnecessary re-renders
  const prevGridRef = useRef<GuessGrid | undefined>(undefined);
  const [updateGrid, setUpdateGrid] = useState<GuessGrid | undefined>(undefined);

  useEffect(() => {
    const newGrid = gameState?.grid ? convexGridToGuessGrid(gameState.grid) : undefined;

    // Only update if grid data actually changed (deep equality check)
    if (!gridsEqual(prevGridRef.current, newGrid)) {
      console.log("Remote grid changed, updating");
      prevGridRef.current = newGrid;
      setUpdateGrid(newGrid);
    }
  }, [gameState?.grid]);

  // Check if puzzle is complete
  useEffect(() => {
    if (!hasShownCongrats.current && gameState?.grid) {
      const isComplete = isPuzzleComplete(gameState.grid, puzzleData);
      if (isComplete) {
        console.log("Puzzle complete! Showing congratulations");
        hasShownCongrats.current = true;
        setShowCongrats(true);
      }
    }
  }, [gameState?.grid, puzzleData]);

  // Handle cell changes from the crossword component
  const handleCellChange = useCallback(
    (cellChange: CellChange) => {
      console.log("Cell changed", cellChange);

      // Send this cell change immediately to Convex
      updateMultipleCells({
        sessionId,
        cells: [{
          x: cellChange.pos.col,
          y: cellChange.pos.row,
          value: cellChange.guess || "",
          pencil: false,
        }],
        userId,
      });
    },
    [sessionId, userId, updateMultipleCells]
  );

  // Handle cell focus (cursor position) changes
  const handleCellFocus = useCallback(
    (cellFocus: CellFocus) => {
      // Send presence update with cursor position
      heartbeat({
        sessionId,
        userId,
        displayName,
        cursorX: cellFocus.pos.col,
        cursorY: cellFocus.pos.row,
        direction: cellFocus.clueId.includes("across") ? "across" : "down",
      });
    },
    [sessionId, userId, displayName, heartbeat]
  );

  // Periodic presence heartbeat
  useEffect(() => {
    const interval = setInterval(() => {
      heartbeat({
        sessionId,
        userId,
        displayName,
        cursorX: undefined,
        cursorY: undefined,
        direction: undefined,
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [sessionId, userId, displayName, heartbeat]);

  // Convert presences to remote cursors format for mycrossword
  const remoteCursors = useMemo(() => {
    return presences
      ?.filter((p) => p.userId !== userId)
      .map((p) => ({
        userId: p.userId,
        x: p.cursorX,
        y: p.cursorY,
        color: p.color,
      })) || [];
  }, [presences, userId]);

  return (
    <div className="relative max-w-4xl mx-auto">
      <div className="mb-4 flex justify-end">
        <button
          onClick={() => setErrorCheckMode(!errorCheckMode)}
          className={`px-4 py-2 rounded-md font-medium transition-colors ${
            errorCheckMode
              ? "bg-red-600 text-white hover:bg-red-700"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {errorCheckMode ? "Error Check: ON" : "Error Check: OFF"}
        </button>
      </div>
      <MyCrossword
        id={`crossword-${sessionId}`}
        data={puzzleData}
        loadGrid={initialLoadGrid}
        updateGrid={updateGrid}
        remoteCursors={remoteCursors}
        errorCheckMode={errorCheckMode}
        onCellChange={handleCellChange}
        onCellFocus={handleCellFocus}
      />
      <ConfettiPopup
        show={showCongrats}
        onClose={() => setShowCongrats(false)}
      />
    </div>
  );
}
